import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import LoginPage from "./pages/LoginPage";
import CounselorDashboard from "./pages/CounselorDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import SupervisorDashboard from "./pages/SupervisorDashboard";
import SupervisorLanding from "./pages/SupervisorLanding";
import PoolDashboard from "./pages/PoolDashboard";
import AthleticsDashboard from "./pages/AthleticsDashboard";
import HealthDashboard from "./pages/HealthDashboard";
import DietaryLanding from "./pages/DietaryLanding";
import SpecialistRoute from "./pages/SpecialistRoute";
import { AuthProvider, useAuth } from "./context/AuthContext";
import TutorialCounselor from "./pages/TutorialCounselor";
import TutorialSupervisors from "./pages/TutorialSupervisors";
import SmsTerms from "./pages/SmsTerms";
import SmsOptIn from "./pages/SmsOptIn";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import ParentSurveyPage from "./pages/ParentSurveyPage";
import TutorialBus from "./pages/TutorialBus";
import TutorialAttendance from "./pages/TutorialAttendance";
import TutorialDining from "./pages/TutorialDining";
import TutorialGames from "./pages/TutorialGames";
import HelpPage from "./pages/HelpPage";
import TripsPage from "./pages/TripsPage";

const ProtectedRoute = ({ children, requireAdmin = false }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (requireAdmin && user.user_type !== "admin") {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function DietaryRedirect() {
  // Legacy redirect (no longer used) — kept for reference only.
  // The /dietary route now renders <DietaryLanding /> with two cards.
  return null;
}

function MirrorReturnButton() {
  const returnToken = localStorage.getItem("admin_return_token");
  const current = localStorage.getItem("token");
  if (!returnToken || returnToken === current) return null;
  return (
    <button
      data-testid="return-to-admin-btn"
      onClick={() => {
        localStorage.setItem("token", returnToken);
        localStorage.removeItem("admin_return_token");
        window.location.href = "/admin";
      }}
      style={{ position: "fixed", bottom: 16, right: 16, zIndex: 9999 }}
      className="px-4 py-2 rounded-full bg-slate-900 text-white text-xs font-bold shadow-lg border-2 border-amber-400 hover:bg-slate-800"
    >
      ← Return to Admin
    </button>
  );
}

function AppRoutes() {
  const { user } = useAuth();
  
  return (
    <>
    <Routes>
      <Route path="/login" element={user ? <Navigate to={user.user_type === "admin" ? "/admin" : user.user_type === "supervisor" ? "/supervisor" : user.user_type === "pool" ? "/pool" : user.user_type === "dietary" ? "/dietary" : user.user_type === "athletics" ? "/athletics" : user.user_type === "health" ? "/health" : user.user_type === "specialist" ? "/specialist" : "/dashboard"} replace /> : <LoginPage />} />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <CounselorDashboard />
        </ProtectedRoute>
      } />
      <Route path="/pool" element={
        <ProtectedRoute>
          <PoolDashboard onLogout={() => { localStorage.removeItem("token"); window.location.href = "/login"; }} />
        </ProtectedRoute>
      } />
      <Route path="/dietary" element={
        <ProtectedRoute>
          <DietaryLanding onLogout={() => { localStorage.removeItem("token"); window.location.href = "/login"; }} />
        </ProtectedRoute>
      } />
      <Route path="/athletics" element={
        <ProtectedRoute>
          <AthleticsDashboard onLogout={() => { localStorage.removeItem("token"); window.location.href = "/login"; }} />
        </ProtectedRoute>
      } />
      <Route path="/health" element={
        <ProtectedRoute>
          <HealthDashboard onLogout={() => { localStorage.removeItem("token"); window.location.href = "/login"; }} />
        </ProtectedRoute>
      } />
      <Route path="/specialist" element={<SpecialistRoute />} />
      <Route path="/admin" element={
        <ProtectedRoute requireAdmin>
          <AdminDashboard />
        </ProtectedRoute>
      } />
      <Route path="/supervisor" element={<SupervisorLanding />} />
      <Route path="/supervisor/dashboard" element={
        <ProtectedRoute>
          <SupervisorDashboard />
        </ProtectedRoute>
      } />
      <Route path="/tutorialcounselor" element={<TutorialCounselor />} />
      <Route path="/tutorialsupervisors" element={<TutorialSupervisors />} />
      <Route path="/sms-terms" element={<SmsTerms />} />
      <Route path="/sms-optin" element={<SmsOptIn />} />
      <Route path="/sms-consent" element={<Navigate to="/sms-optin" replace />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/parentsurvey" element={<ParentSurveyPage />} />
      <Route path="/privacy" element={<Navigate to="/privacy-policy" replace />} />
      <Route path="/tutorialbus" element={<TutorialBus />} />
      <Route path="/tutorialattendance" element={<TutorialAttendance />} />
      <Route path="/tutorialdining" element={<TutorialDining />} />
      <Route path="/tutorialgames" element={<TutorialGames />} />
      <Route path="/help" element={<HelpPage />} />
      <Route path="/trips" element={<TripsPage />} />
      <Route path="/" element={<Navigate to="/login" replace />} />
    </Routes>
    <MirrorReturnButton />
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
